/**
 * 提现相关 API：平台余额、支持的链/代币（与充值共用 rc 接口）。
 */

import { getWalletDetail } from '@/api/trade';
import { readWalletAvailable, formatDisplayCurrencyLabel } from '@/utils/platform-balance';

const BALANCE_FETCH_RETRIES = 2;
const BALANCE_FETCH_RETRY_DELAY_MS = 400;
const PLATFORM_BALANCE_CACHE_MS = 10_000;
/** wallet/detail：平台钱包尚未创建，不应走 RC 余额兜底 */
const WALLET_NOT_FOUND_CODE = 601;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** @type {{ key: string, expires: number, value: object|null, promise: Promise<object>|null }} */
const platformBalanceCache = {
  key: '',
  expires: 0,
  value: null,
  promise: null,
};

export function invalidatePlatformBalanceCache() {
  platformBalanceCache.key = '';
  platformBalanceCache.expires = 0;
  platformBalanceCache.value = null;
  platformBalanceCache.promise = null;
}

function normalizePlatformBalanceResponse(data, currency, meta = {}) {
  const normalizedAvailable = readWalletAvailable(data);
  const apiCurrency = String(data?.currency || currency || '').trim();
  // 对用户展示 USD；接口 currency 仍保留底层 DUSD/USDC
  const displaySymbol = formatDisplayCurrencyLabel(apiCurrency);

  return {
    balanceUsd: normalizedAvailable,
    available: normalizedAvailable,
    frozen: Number(data?.frozen ?? 0),
    total: Number(data?.total ?? normalizedAvailable),
    currency: apiCurrency,
    displayCurrency: displaySymbol,
    tokenId: meta.tokenId ?? '',
    source: meta.source ?? 'wallet',
    wallet: data,
    tokens: [
      {
        symbol: displaySymbol,
        amount: normalizedAvailable,
        usd: normalizedAvailable,
        color: '#2775ca',
      },
    ],
  };
}

function isWalletNotFoundError(err) {
  if (Number(err?.code) === WALLET_NOT_FOUND_CODE) return true;
  const msg = String(err?.message ?? '').trim();
  if (msg === '钱包不存在') return true;
  return msg.includes('钱包不存在') && /\b601\b/.test(msg);
}

function emptyPlatformBalanceResponse(currency, meta = {}) {
  const sym = String(currency ?? '').trim();
  return normalizePlatformBalanceResponse(
    { available: 0, frozen: 0, total: 0, currency: sym },
    sym,
    { source: 'wallet-not-found', ...meta }
  );
}

/** 平台余额由服务端选择账本币种，不回退外部钱包余额。 */
async function fetchPlatformBalanceInner() {
  const sym = '';
  let lastError = null;

  for (let attempt = 0; attempt <= BALANCE_FETCH_RETRIES; attempt += 1) {
    try {
      const data = await getWalletDetail();
      return normalizePlatformBalanceResponse(data, sym, { source: 'wallet-detail' });
    } catch (err) {
      lastError = err;
      if (isWalletNotFoundError(err)) {
        return emptyPlatformBalanceResponse(sym);
      }
      console.warn('[platform-balance] wallet/detail failed', err);
      if (attempt < BALANCE_FETCH_RETRIES) {
        await sleep(BALANCE_FETCH_RETRY_DELAY_MS);
      }
    }
  }

  throw lastError ?? new Error('platform balance fetch failed');
}

export async function fetchPlatformBalance(options = {}) {
  const force = options?.force === true;
  const cacheKey = 'platform';
  const now = Date.now();

  if (
    !force &&
    platformBalanceCache.key === cacheKey &&
    platformBalanceCache.expires > now &&
    platformBalanceCache.value
  ) {
    return platformBalanceCache.value;
  }

  if (!force && platformBalanceCache.promise) {
    return platformBalanceCache.promise;
  }

  const request = fetchPlatformBalanceInner()
    .then((result) => {
      platformBalanceCache.key = cacheKey;
      platformBalanceCache.value = result;
      platformBalanceCache.expires = Date.now() + PLATFORM_BALANCE_CACHE_MS;
      platformBalanceCache.promise = null;
      return result;
    })
    .catch((err) => {
      platformBalanceCache.promise = null;
      throw err;
    });

  platformBalanceCache.promise = request;
  return request;
}
